import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NgbPaginationModule, NgbTooltipModule, NgbAlertModule, NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { CreateUserModalComponent } from '../create-user-modal/create-user-modal.component';

// Staff interface for staff management
export interface StaffDetails {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position: string;
  department: string;
  role: 'admin' | 'staff' | 'manager';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastLogin?: string;
}

// Staff filters
export interface StaffFilters {
  searchTerm: string;
  department: string;
  position: string;
  role: string;
  status: 'all' | 'active' | 'inactive';
}

@Component({
  selector: 'app-staff-management',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    NgbPaginationModule, 
    NgbTooltipModule, 
    NgbAlertModule,
    NgbDropdownModule,
    StaffHeaderComponent, 
    StaffNavComponent
  ],
  templateUrl: './staff-management.component.html',
  styleUrl: './staff-management.component.scss'
})
export class StaffManagementComponent implements OnInit, OnDestroy {
  currentUser: AuthUser | null = null;
  isMobileMenuOpen = false;
  private userSubscription: Subscription | undefined;
  
  staff: StaffDetails[] = [];
  filteredStaff: StaffDetails[] = [];
  departments: string[] = ['IT', 'HR', 'Finance', 'Operations', 'Management'];
  positions: string[] = ['Developer', 'Manager', 'Analyst', 'Coordinator', 'Director'];
  roles: string[] = ['admin', 'staff', 'manager'];
  isLoading = false;
  showMockDataWarning = true;
  
  filters: StaffFilters = {
    searchTerm: '',
    department: '',
    position: '',
    role: '',
    status: 'all'
  };

  // Pagination
  page = 1;
  pageSize = 10;
  readonly pageSizeOptions = [10, 20, 50, 100];
  collectionSize = 0;
  
  // Sorting
  sortField = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  constructor(
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private modal: NgbModal
  ) {}

  ngOnInit(): void {
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });
    
    this.loadStaff();
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  /**
   * Load staff data (mock implementation)
   */
  loadStaff(): void {
    this.isLoading = true;
    
    // Simulate API call with mock data
    setTimeout(() => {
      this.staff = this.generateMockStaffData();
      this.showMockDataWarning = true;
      this.applyFilters();
      this.isLoading = false;
      this.cdr.detectChanges();
    }, 1000);
  }

  /**
   * Generate mock staff data
   */
  private generateMockStaffData(): StaffDetails[] {
    return [
      {
        id: '1',
        firstName: 'สมชาย',
        lastName: 'ใจดี',
        email: 'somchai.jaidee@company.com',
        phone: '081-234-5678',
        position: 'Manager',
        department: 'IT',
        role: 'manager',
        isActive: true,
        createdAt: '2024-01-15',
        updatedAt: '2024-10-20',
        lastLogin: '2024-10-26'
      },
      {
        id: '2',
        firstName: 'สุดา',
        lastName: 'มั่นคง',
        email: 'suda.mankong@company.com',
        phone: '082-345-6789',
        position: 'Developer',
        department: 'IT',
        role: 'staff',
        isActive: true,
        createdAt: '2024-02-10',
        updatedAt: '2024-10-25',
        lastLogin: '2024-10-25'
      },
      {
        id: '3',
        firstName: 'วิชัย',
        lastName: 'สุขใส',
        email: 'wichai.suksai@company.com',
        phone: '083-456-7890',
        position: 'Analyst',
        department: 'Finance',
        role: 'staff',
        isActive: true,
        createdAt: '2024-03-05',
        updatedAt: '2024-10-24',
        lastLogin: '2024-10-24'
      },
      {
        id: '4',
        firstName: 'นิภา',
        lastName: 'เก่งกาจ',
        email: 'nipha.kengkaj@company.com',
        phone: '084-567-8901',
        position: 'Coordinator',
        department: 'HR',
        role: 'staff',
        isActive: true,
        createdAt: '2024-04-12',
        updatedAt: '2024-10-23',
        lastLogin: '2024-10-23'
      },
      {
        id: '5',
        firstName: 'ประยุทธ',
        lastName: 'มุ่งมั่น',
        email: 'prayuth.mungman@company.com',
        phone: '085-678-9012',
        position: 'Director',
        department: 'Management',
        role: 'admin',
        isActive: true,
        createdAt: '2024-01-01',
        updatedAt: '2024-10-26',
        lastLogin: '2024-10-26'
      },
      {
        id: '6',
        firstName: 'อรุณี',
        lastName: 'สว่างใส',
        email: 'arunee.sawangsai@company.com',
        phone: '086-789-0123',
        position: 'Manager',
        department: 'Operations',
        role: 'manager',
        isActive: false,
        createdAt: '2024-05-20',
        updatedAt: '2024-10-15',
        lastLogin: '2024-10-10'
      }
    ];
  }

  /**
   * Apply filters to staff list
   */
  applyFilters(): void {
    let filtered = [...this.staff];

    // Search term filter
    if (this.filters.searchTerm) {
      const searchLower = this.filters.searchTerm.toLowerCase();
      filtered = filtered.filter(staff => 
        staff.firstName.toLowerCase().includes(searchLower) ||
        staff.lastName.toLowerCase().includes(searchLower) ||
        staff.email.toLowerCase().includes(searchLower) ||
        staff.phone.includes(searchLower)
      );
    }

    // Department filter
    if (this.filters.department) {
      filtered = filtered.filter(staff => staff.department === this.filters.department);
    }

    // Position filter
    if (this.filters.position) {
      filtered = filtered.filter(staff => staff.position === this.filters.position);
    }

    // Role filter
    if (this.filters.role) {
      filtered = filtered.filter(staff => staff.role === this.filters.role);
    }

    // Status filter
    if (this.filters.status !== 'all') {
      const isActive = this.filters.status === 'active';
      filtered = filtered.filter(staff => staff.isActive === isActive);
    }

    this.filteredStaff = filtered;
    this.collectionSize = filtered.length;
    this.page = 1; // Reset to first page when filters change
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      department: '',
      position: '',
      role: '',
      status: 'all'
    };
    this.applyFilters();
  }

  /**
   * Sort staff by field
   */
  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }

    this.filteredStaff.sort((a: any, b: any) => {
      const aVal = a[field];
      const bVal = b[field];
      
      if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  /**
   * Get sort icon for column header
   */
  getSortIcon(field: string): string {
    if (this.sortField !== field) return '↕️';
    return this.sortDirection === 'asc' ? '↑' : '↓';
  }

  /**
   * Get current page staff
   */
  getCurrentPageStaff(): StaffDetails[] {
    const startIndex = (this.page - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.filteredStaff.slice(startIndex, endIndex);
  }

  /**
   * Change page size
   */
  changePageSize(newSize: number): void {
    this.pageSize = newSize;
    this.page = 1;
  }

  /**
   * Toggle staff status
   */
  toggleStaffStatus(staff: StaffDetails): void {
    staff.isActive = !staff.isActive;
    staff.updatedAt = new Date().toISOString().split('T')[0];
    // In real app, this would call an API
  }

  /**
   * Edit staff
   */
  editStaff(staff: StaffDetails): void {
    // TODO: Open edit staff modal
    console.log('Edit staff:', staff);
  }

  /**
   * Delete staff
   */
  deleteStaff(staff: StaffDetails): void {
    if (confirm(`คุณต้องการลบเจ้าหน้าที่ ${staff.firstName} ${staff.lastName} หรือไม่?`)) {
      this.staff = this.staff.filter(s => s.id !== staff.id);
      this.applyFilters();
    }
  }

  /**
   * Create new staff
   */
  createNewStaff(): void {
    // TODO: Open create staff modal
    console.log('Create new staff');
  }

  /**
   * Export to CSV
   */
  exportToCSV(): void {
    const headers = ['ชื่อ', 'นามสกุล', 'อีเมล', 'โทรศัพท์', 'ตำแหน่ง', 'แผนก', 'บทบาท', 'สถานะ', 'วันที่สร้าง'];
    const csvData = this.filteredStaff.map(staff => [
      staff.firstName,
      staff.lastName,
      staff.email,
      staff.phone,
      staff.position,
      staff.department,
      staff.role,
      staff.isActive ? 'ใช้งาน' : 'ไม่ใช้งาน',
      staff.createdAt
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `staff_data_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }

  /**
   * Get status class for badge
   */
  getStatusClass(isActive: boolean): string {
    return isActive ? 'bg-success' : 'bg-secondary';
  }

  /**
   * Get role badge class
   */
  getRoleBadgeClass(role: string): string {
    switch (role) {
      case 'admin': return 'bg-danger';
      case 'manager': return 'bg-warning';
      case 'staff': return 'bg-info';
      default: return 'bg-secondary';
    }
  }

  /**
   * Track by function for ngFor
   */
  trackByStaffId(index: number, staff: StaffDetails): string {
    return staff.id;
  }

  /**
   * Utility function for min value
   */
  min(a: number, b: number): number {
    return Math.min(a, b);
  }

  /**
   * Handle logout
   */
  onLogout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  /**
   * Toggle mobile menu
   */
  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  /**
   * Close mobile menu
   */
  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  /**
   * Handle navigation
   */
  onNavigate(route: string): void {
    this.router.navigate([`/staff/${route}`]);
  }
}
